__webpack_public_path__ =
  process.env.ENV === 'development'
    ? process.env.WEBPACK_PUBLIC_PATH
    : `${window.$mediavine.web.model.wrapperSrc}/`
import Blocklist from './blocklist/Blocklist'
import { domReady, writeCss, addClass } from './helpers/DOM'
import Logger from './helpers/Logger'
import WebModel, {
  optimizingForDesktopPagespeed,
  optimizingForMobilePagespeed
} from './models/WebModel'
import PageSettings from './pageSettings/PageSettings'
import DynamicContentSlots from './renderers/DynamicContentSlots'
import SlotFactory from './slots/SlotFactory'
import StickyVideo, {
  VIDEO_TARGET_CLASS,
  VIDEO_HEADLINE_CLASS
} from './stickyVideo/StickyVideo'
import { deviceIndicatorPrefix, getBaseStyles } from './styles/base'
import { AdReporter } from './adReporter/AdReporter'
import { doOnce } from './helpers/doOnce'
import JumpToRecipe from './jumpToRecipe/JumpToRecipe'
// import { Session } from './session/Session'
// import { UTM } from './helpers/UTM'
import { ArrivalSlot } from './renderers/ArrivalSlot'
import { USP } from './cmp/usp/USP'
import MetricsTracker from './metrics/MetricsTracker'
import GrowFaves from './growFaves/GrowFaves'
import { getLCP, getFID } from 'web-vitals'
import { adBoxSelector } from './slots/helpers/AdBox'
import { Device } from './helpers'
import { queryParamHas } from './helpers/getQueryParam'

export const GDPRStub: IGDPR = {
  enableGdpr: false,
  onReady: (cb) => cb(),
  onNoConsent: (cb) => () => {},
  initModal: () => {}
}

if (window.$mediavine.web.model.gdpr === '1') {
  import(/* webpackChunkName: "gdpr"*/ './cmp/tcf/TcfStub')
    .then(({ makeStub }) => makeStub())
    .catch((error) => {
      const errorMessage = 'GDPR: Error loading "gdpr" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    })
}

const debug = (msg: string) => Logger.custom(['debug', 'wrapper'], msg)

let model: WebModel
let ccpa
let gdpr: IGDPR = GDPRStub

const reRenderables: Array<{ render: Function }> = []

export enum ExecutionStatus {
  halted,
  delayed,
  executed
}

export const init = async (): Promise<ExecutionStatus> => {
  if (window.$mediavine.web.initialized) {
    debug('Wrapper previously initialized. Halting Execution.')

    return ExecutionStatus.halted
  }

  domReady(() => {
    addClass(document.body, 'mv-loaded')
    addClass(document.body, `${deviceIndicatorPrefix}${Device.deviceType}`)
  })
  window.$mediavine.web.initialized = true

  window.$mediavine.web.load = doOnce(async () => {
    const { load } = await import(
      /* webpackChunkName: "afterScroll"*/ './afterScroll'
    )
    return load(model, blocklist, slotFactory, gdpr, reRenderables)
  })

  new AdReporter()

  model = new WebModel(window.$mediavine.web.model)

  new MetricsTracker(model)
  if (model.gdpr === '1') {
    try {
      const [{ TCF }, { GDPR }] = await Promise.all([
        import(/* webpackChunkName: "gdpr"*/ './cmp/tcf/TCF'),
        import(/* webpackChunkName: "gdpr"*/ './cmp/gdpr/GDPR')
      ])
      await TCF.init()
      gdpr = new GDPR()
      await gdpr.initModal()
    } catch (error) {
      const errorMessage = 'GDPR: Error loading "gdpr" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    }
  } else {
    window.dispatchEvent(
      new CustomEvent('mediavineCmp', { detail: 'gdprDoesNotApply' })
    )
  }

  if (euNoAds(model)) {
    debug('Wrapper not inited because site does not serve ads in EU')
    return ExecutionStatus.halted
  } else if (onPrivacyPolicyPage(model)) {
    debug('Wrapper not inited because we are on the privacy policy page.')
    return ExecutionStatus.halted
  } else if (model.launch_mode) {
    handleLaunchMode(model.mcmNetworkCode)
    if (!queryParamHas('test', 'placeholders') && !model.testSite) {
      debug('Wrapper not inited because the site is in Launch Mode.')
      return ExecutionStatus.halted
    }
  }

  USP.init(model)

  if (model.ccpa === '1') {
    try {
      const [{ CCPA }] = await Promise.all([
        import(/* webpackChunkName: "ccpa"*/ './cmp/ccpa/CCPA')
      ])
      ccpa = new CCPA(model)
      await ccpa.initCCPA()
    } catch (error) {
      const errorMessage = 'CCPA: Error loading "ccpa" webpackChunk'
      Logger.custom(['debug', 'ccpa'], errorMessage)
      window.$mediavine.newrelic.addPageAction('ccpaError', {
        error,
        errorMessage
      })
    }
  }

  writeCss(getBaseStyles(model))
  const pageSettings = new PageSettings()
  const blocklist = new Blocklist(model, pageSettings)
  const slotFactory = new SlotFactory(model, blocklist)
  new GrowFaves(model)
  const dynamicContentSlots = new DynamicContentSlots(model, slotFactory)
  reRenderables.push(dynamicContentSlots)
  dynamicContentSlots.renderedPromise.then(() => {
    // Need to wait for the Content slots to by inserted before placing the StickyVideo
    reRenderables.push(new StickyVideo(model, blocklist))
    hidePlaceholdersOnNoConsent(gdpr)
    ArrivalSlot.attachListener(model, window.$mediavine.web.load)
  })

  new JumpToRecipe(model)

  if (optimizingForMobilePagespeed() || optimizingForDesktopPagespeed()) {
    debug('Pagespeed Optimization. Delaying execution until scroll.')

    const callback = () => {
      debug('Scroll detected or waited too long. Executing.')
      window.removeEventListener('scroll', callback, false)
      window.removeEventListener('beforeprint', callback, false)

      window.$mediavine.web.load()
    }

    getLCP(() => {
      callback()
    }, false)

    getFID(() => {
      callback()
    }, false)

    window.addEventListener('scroll', callback, false)
    window.addEventListener('beforeprint', callback, false)

    // Alternatively, force load after 3 seconds of waiting for scroll
    // const loadAfterMs = 3100 //ms
    // setTimeout(callback, loadAfterMs)

    return ExecutionStatus.delayed
  } else {
    window.$mediavine.web.load()

    return ExecutionStatus.executed
  }
}

window.$mediavine.web.init = init

const euNoAds = (model: WebModel): boolean => {
  const isEU = model.gdpr === '1'
  const gdprNonCompliant = model.gdpr_compliance !== 1
  return isEU && gdprNonCompliant
}

export const validate = (bool: boolean, errMessage: string): boolean => {
  if (bool) {
    debug(`${errMessage}. Halting execution.`)
  }
  return bool
}

export const onPrivacyPolicyPage = (model): boolean =>
  validate(
    model.privacy_policy_link ===
      `${window.location.origin}${window.location.pathname}`,
    "On site's privacy page."
  )
async function handleLaunchMode(mcmNetworkCode) {
  const { default: loadjs } = await import('loadjs')
  loadjs('https://securepubads.g.doubleclick.net/tag/js/gpt.js')

  const mcm_child_id = mcmNetworkCode
  window.googletag = window.googletag || { cmd: [] }
  googletag.cmd.push(function () {
    googletag.pubads().disableInitialLoad()
    googletag.enableServices()
  })
  const el = document.createElement('div')
  el.id = 'mcm-verification'
  document.body.appendChild(el)
  googletag.cmd.push(function () {
    const mcm_slot = googletag
      .defineSlot(
        `/1030006,${mcm_child_id}/verification`,
        [1, 1],
        'mcm-verification'
      )
      .addService(googletag.pubads())
    googletag.display('mcm-verification')
    googletag.pubads().refresh([mcm_slot])
  })
}

// Hide the adBoxes, hint placeholders, Video Target Divs, and Video Headlines if there is no GDPR consent
function hidePlaceholdersOnNoConsent(gdpr: IGDPR) {
  gdpr.onNoConsent(() => {
    const selectors = [
      `.${adBoxSelector}`,
      '[data-slot]',
      `.${VIDEO_TARGET_CLASS}`,
      `.${VIDEO_HEADLINE_CLASS}`
    ].join(', ')

    // We're writing CSS incase things like Hints are added dynamically to the page after this code gets run.
    writeCss(`${selectors} { display: none !important;}`)
  })
}
